import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
  Pagination,
  Button,
  styled,
  tableCellClasses,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  ListItemIcon,
  Stack,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import * as XLSX from "xlsx";
import { MondayContext } from "../Contexts/MondayContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#117E76",
    color: theme.palette.common.white,
    fontSize: 20,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 18,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const FFTable = () => {
  const [tableData, setTableData] = useState({ headers: [], rows: [] });
  const [filteredRows, setFilteredRows] = useState([]);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10; // Fixed rows per page
  const { commentsData } = useContext(MondayContext);

  // Filters
  const [focusAreaFilter, setFocusAreaFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [focusAreaOpen, setFocusAreaOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [filterDateRange, setFilterDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!commentsData || commentsData.length === 0) return;
        // 1. Define headers
        const headers = Object.keys(commentsData[0]);
        headers.push("Caterory List"); // Add Category List column

        // 2. Define key references
        const dateKey = "CommentDate";
        const categoryNameKey = "CategoryName";
        const otherCategoryKey = "OtherCategory";

        // 3. Process rows
        const rows = commentsData.map((item) => {
          // --- Format date to MM/DD/YY ---
          let formattedDate = item[dateKey];
          if (formattedDate && !isNaN(Date.parse(formattedDate))) {
            formattedDate = new Date(formattedDate).toLocaleDateString(
              "en-US",
              {
                timeZone: "UTC",
                year: "2-digit",
                month: "2-digit",
                day: "2-digit",
              }
            );
          }

          // --- Build category list ---
          const categoryList = item[categoryNameKey]
            ? item[categoryNameKey]
                .split(",")
                .map((cat) => cat.trim())
                .filter((cat) => cat.toLowerCase() !== "other")
            : [];

          if (item[otherCategoryKey]) {
            categoryList.push(item[otherCategoryKey]);
          }

          // --- Replaceing formatted date in the row array and cleaning ---
          const row = headers.slice(0, -1).map((key) => {
            if (key === dateKey) return formattedDate;
            return item[key] ?? "";
          });

          // --- Append the category list column ---
          row.push(categoryList);

          return row;
        });

        // 4. Save to state
        setTableData({ headers, rows });
        setFilteredRows(rows);
      } catch (error) {
        console.error("Error processing backend data :", error);
      }
    };

    fetchData();
  }, [commentsData]);

  // Extract column indices for sorting and filtering
  const CommentIndex = tableData.headers.indexOf("Comments");
  const dateIndex = tableData.headers.indexOf("CommentDate");
  const focusAreaIndex = tableData.headers.indexOf("CommentLocation");
  const OtherAreaIndex = tableData.headers.indexOf("OtherLocation");
  // const OtherCategoryIndex = tableData.headers.indexOf("OtherCategory");
  const CategoryNameIndex = tableData.headers.indexOf("CategoryName");
  const FullCategoryListIndex = tableData.headers.indexOf("Caterory List");

  const focusAreaOptions = [
    "Campville",
    "Cross Creek",
    "Earleton",
    "East County Generally",
    "Evinston",
    "Gainesville",
    "Gordon",
    "Hawthorne",
    "Island Grove",
    "Lochloosa",
    "Melrose",
    "Micanopy",
    "Monteocha",
    "Orange Heights",
    "Orange Lake",
    "Rochelle",
    "Waldo",
    "Windsor",
    "Other",
  ];

  const categoryOptions = [
    "Agriculture",
    "Animal Services",
    "Community Identity/Marketing",
    "Development Regulations",
    "Economic Development",
    "Education",
    "Employment/Labor",
    "Food",
    "Emergency Management",
    "Government Relations",
    "Housing",
    "Law Enforcement",
    "Natural Resource Protection",
    "Parks and Recreation",
    "Public Engagement",
    "Public Health",
    "Public Safety",
    "Transportation/Roads",
    "Traffic Safety",
    "Senior Services",
    "Social Services",
    "Water and Sewer Systems",
    "Youth Services",
    "Zoning",
    "Other",
  ];

  // Handle Focus Area Filter (CommentLocation)
  const handleFocusAreaChange = (event) => {
    let selectedAreas = event.target.value;
    if (selectedAreas.includes("__close__")) {
      setFocusAreaOpen(false);
      selectedAreas = selectedAreas.filter((val) => val !== "__close__");
    }
    setFocusAreaFilter(selectedAreas);
    applyFilters(selectedAreas, filterDateRange, categoryFilter);
    setPage(1);
  };

  // Handle Category Filter
  const handleCategoryChange = (event) => {
    let selectedCategory = event.target.value;
    if (selectedCategory.includes("__close__")) {
      setCategoryOpen(false);
      selectedCategory = selectedCategory.filter((val) => val !== "__close__");
    }
    setCategoryFilter(selectedCategory);
    applyFilters(focusAreaFilter, filterDateRange, selectedCategory);
    setPage(1);
  };

  const handleDateFilterChange = (newDate, fieldName) => {
    const newDateRange = { ...filterDateRange, [fieldName]: newDate }; // Update the state with the new date object
    console.log(newDateRange);
    setFilterDateRange(newDateRange); // Store dayjs object directly in state
    // Apply the filters or any other logic based on the date range
    applyFilters(focusAreaFilter, newDateRange, categoryFilter);
    setPage(1); // Reset to the first page whenever the date range changes
  };

  // Apply Filters
  const applyFilters = (focusAreas, dateRange, categories) => {
    let filtered = tableData.rows;

    // Filter by Focus Area (text-based includes matching)
    if (focusAreas.length > 0) {
      filtered = filtered.filter((row) =>
        focusAreas.some((focus) =>
          row[focusAreaIndex]?.toLowerCase().includes(focus.toLowerCase())
        )
      );
    }

    // Filter by Categories (text-based includes matching)
    if (categories.length > 0) {
      filtered = filtered.filter((row) =>
        categories.some((focus) =>
          row[CategoryNameIndex]?.toLowerCase().includes(focus.toLowerCase())
        )
      );
    }

    // Filter by Date Range
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter((row) => {
        const rowDate = dayjs(row[dateIndex]); // Convert row date to dayjs object
        const startDate = dateRange.startDate
          ? dayjs(dateRange.startDate)
          : null;
        const endDate = dateRange.endDate ? dayjs(dateRange.endDate) : null;

        return (
          (!startDate ||
            rowDate.isAfter(startDate) ||
            rowDate.isSame(startDate)) && // Check if rowDate is after or equal to startDate
          (!endDate || rowDate.isBefore(endDate) || rowDate.isSame(endDate)) // Check if rowDate is before or equal to endDate
        );
      });
    }

    setFilteredRows(filtered);
  };

  // Reset filtered rows to original data
  const handleResetFilters = () => {
    setCategoryFilter([]); // Clear category filter
    setFocusAreaFilter([]); // Clear focus area filter
    setPage(1); // Reset pagination to first page
    setFilteredRows(tableData.rows); // Reset filtered rows to original data
    setFilterDateRange({ startDate: null, endDate: null }); // Reset date range
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (!tableData || !tableData.headers || !tableData.rows) return;

    // Define which columns to include (and in what order)
    const exportHeaders = [
      "CommentDate",
      "Comments",
      "CommentLocation",
      "CategoryName",
    ];

    // Find indexes of those headers in your tableData
    const indexesToKeep = exportHeaders.map((header) =>
      tableData.headers.indexOf(header)
    );

    // Build filtered data rows
    const exportRows = tableData.rows.map((row) =>
      indexesToKeep.map((i) => {
        const cell = row[i];
        return Array.isArray(cell) ? cell.join(", ") : cell;
      })
    );

    // Combine headers + filtered rows
    const exportData = [exportHeaders, ...exportRows];

    // Create sheet and workbook
    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Comments Data");

    // Export to file
    XLSX.writeFile(
      workbook,
      `CommentsData_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Calculate total pages
  const totalRows = filteredRows.length;
  const pageCount = Math.ceil(totalRows / rowsPerPage);

  // Calculate start & end row indexes
  const startRow = (page - 1) * rowsPerPage + 1;
  const endRow = Math.min(page * rowsPerPage, totalRows);

  // Slice data for pagination
  const displayedRows = filteredRows.slice(startRow - 1, endRow);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    // <Paper sx={{ padding: 2 }}>
    <TableContainer component={Paper}>
      {commentsData ? (
        <span>
          {/* Header and filters */}
          <Box sx={{ mt: 2 }}>
            {/* Top Row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                textAlign: { xs: "center", sm: "left" },
                gap: 2,
              }}
            >
              {/* Left side: Showing and Pagination stacked */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", sm: "flex-start" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    pl: { xs: 0, sm: 2 }, // Adds left padding only on tablets and up
                    pb: { xs: 0, sm: 2 }, // Adds bottom padding only on tablets and up
                  }}
                >
                  Showing {startRow}–{endRow} of {totalRows}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePageChange}
                  boundaryCount={1}
                  // size="small"
                  shape="rounded"
                  variant="outlined"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#117E76",
                    },
                    "& .MuiPaginationItem-outlined": {
                      backgroundColor: "#fcfcfc",
                      borderColor: "#6d6d6d",
                    },
                    "& .MuiPaginationItem-page.Mui-selected": {
                      backgroundColor: "#117E76",
                      color: "white",
                      borderColor: "black",
                    },
                    pl: { xs: 0, sm: 1 }, // Adds left padding only on tablets and up
                    pb: { xs: 0, sm: 1 }, // Adds bottom padding only on tablets and up
                  }}
                />
              </Box>

              {/* Right side: Buttons side by side */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: { xs: "center", sm: "flex-end" },
                  paddingRight: { xs: 0, sm: 2 }, // Adds right padding only on tablets and up
                  alignSelf: { sm: "end" },
                  pb: { xs: 0, sm: 1.25 }, // Adds bottom padding only on tablets and up
                }}
              >
                <Button
                  variant="outlined"
                  className="public-commnets-filter-button"
                  sx={{
                    minWidth: 130,
                  }}
                  onClick={handleToggle}
                >
                  {expanded ? "Hide Filters" : "Show Filters"}
                </Button>

                {/* <Link
                  href={commentsExcelFile}
                  target="_blank"
                  rel="noreferrer"
                  download="Forward Focus Public Comments"
                  style={{ textDecoration: "none", color: "black!important" }}
                > */}
                <Button
                  variant="outlined"
                  className="public-commnets-filter-button"
                  onClick={handleExportToExcel}
                  sx={{ width: { xs: "100%", sm: "auto" } }} // Full width on mobile
                >
                  Export to Excel
                </Button>
                {/* </Link> */}
              </Box>
            </Box>

            {/* Accordion row below */}
            <Accordion
              slotProps={{ heading: { component: 'div' } }}
              expanded={expanded}
              elevation={0}
              square
              sx={{
                mt: { xs: 2, sm: 0 }, // Add vertical spacing only on mobile
              }}
            >
              <AccordionSummary aria-hidden="true" sx={{ display: "none" }} />
              <AccordionDetails sx={{ paddingLeft: 0, paddingRight: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", sm: "center" },
                    alignItems: { xs: "stretch", sm: "center" },
                    textAlign: { xs: "left", sm: "center" },
                    gap: "6px",
                    mt: 2,
                  }}
                >
                  {/* Focus Area Filter */}
                  <FormControl
                    sx={{
                      minWidth: 150,
                      width: { xs: "100%", sm: "auto" },
                      maxWidth: { sm: 150, md: 280 },
                    }}
                  >
                    <InputLabel>Focus Area</InputLabel>
                    <Select
                      multiple
                      value={focusAreaFilter}
                      open={focusAreaOpen}
                      onOpen={() => setFocusAreaOpen(true)}
                      onClose={() => setFocusAreaOpen(false)}
                      onChange={handleFocusAreaChange}
                      input={<OutlinedInput label="Focus Area" />}
                      renderValue={(selected) => selected.join(", ")}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: "60vh", // Restrict to 60% of viewport height
                            overflowY: "auto",
                          },
                        },
                      }}
                    >
                      <MenuItem
                        value="__close__"
                        sx={{
                          borderBottom: "1px solid",
                          borderTop: "1px solid",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <ListItemIcon>
                          <CloseIcon />
                        </ListItemIcon>
                        <ListItemText primary="Close" />
                      </MenuItem>
                      {focusAreaOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          <Checkbox
                            checked={focusAreaFilter.indexOf(option) > -1}
                          />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Category Filter */}
                  <FormControl
                    sx={{
                      minWidth: 150,
                      width: { xs: "100%", sm: "auto" },
                      maxWidth: { sm: 150, md: 280 },
                    }}
                  >
                    <InputLabel>Category</InputLabel>
                    <Select
                      multiple
                      value={categoryFilter}
                      open={categoryOpen}
                      onChange={handleCategoryChange}
                      onOpen={() => setCategoryOpen(true)}
                      onClose={() => setCategoryOpen(false)}
                      input={<OutlinedInput label="Category" />}
                      renderValue={(selected) => selected.join(", ")}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: "60vh", // Restrict to 60% of viewport height
                            overflowY: "auto",
                          },
                        },
                      }}
                    >
                      <MenuItem
                        value="__close__"
                        sx={{
                          borderBottom: "1px solid",
                          borderTop: "1px solid",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <ListItemIcon>
                          <CloseIcon />
                        </ListItemIcon>
                        <ListItemText primary="Close" />
                      </MenuItem>
                      {categoryOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          <Checkbox
                            checked={categoryFilter.indexOf(option) > -1}
                          />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Date Filters */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div>
                      <DatePicker
                        label="Start Date"
                        value={filterDateRange.startDate} // Use the dayjs object directly for startDate
                        onChange={(newValue) =>
                          handleDateFilterChange(newValue, "startDate")
                        }
                        sx={{
                          minWidth: 130,
                          width: { xs: "100%", sm: 130, md: "auto" },
                          maxWidth: { md: 185 },
                          marginBottom: { xs: "1.5%", sm: 0 },
                          marginRight: { xs: 0, sm: "5px" },
                        }}
                        slotProps={{
                          actionBar: { actions: ["clear", "cancel", "accept"] },
                          field: { clearable: true },
                          textField: { variant: "outlined" },
                        }}
                      />

                      <DatePicker
                        label="End Date"
                        value={filterDateRange.endDate} // Use the dayjs object directly for endDate
                        onChange={(newValue) =>
                          handleDateFilterChange(newValue, "endDate")
                        }
                        sx={{
                          minWidth: 130,
                          width: { xs: "100%", sm: 130, md: "auto" },
                          maxWidth: { md: 185 },
                        }}
                        slotProps={{
                          actionBar: { actions: ["clear", "cancel", "accept"] },
                          field: { clearable: true },
                          textField: { variant: "outlined" },
                        }}
                      />
                    </div>
                  </LocalizationProvider>

                  {/* Reset Button */}
                  <Button
                    variant="outlined"
                    className="public-commnets-filter-button"
                    onClick={handleResetFilters}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          {/* Table */}
          <Table>
            <TableHead>
              <StyledTableRow>
                <StyledTableCell
                  style={{ justifyContent: "center", textAlign: "center" }} // Center align header>
                >
                  Comments
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {displayedRows.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={3} align="center">
                    No data available for the selected filter(s)
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                displayedRows.map((row, rowIndex) => (
                  <StyledTableRow key={rowIndex}>
                    {/* Comment along with Category name if present, with appended other category data if "other" is found  */}
                    <StyledTableCell>
                      <div
                        style={{
                          fontSize: "0.95rem",
                          color: "#0A4D46",
                          fontWeight: "600",
                          marginBottom: "10px",
                        }}
                      >
                        {row[dateIndex]}
                        <span
                          style={{
                            fontSize: "1rem",
                            paddingLeft: "0.4rem",
                            paddingRight: "0.4rem",
                          }}
                        >
                          |
                        </span>
                        {row[focusAreaIndex]
                          ? (() => {
                              const filteredText = row[focusAreaIndex]
                                .split(",") // Split by comma
                                .map((item) => item.trim()) // Trim whitespace
                                .filter(
                                  (item) => item.toLowerCase() !== "other"
                                ) // Remove "other"
                                .join(", "); // Join back into a string

                              return (
                                filteredText +
                                (filteredText && row[OtherAreaIndex]
                                  ? `, ${row[OtherAreaIndex]}`
                                  : row[OtherAreaIndex] || "")
                              );
                            })()
                          : row[OtherAreaIndex] || ""}
                      </div>
                      {row[CommentIndex]}
                      {row[CategoryNameIndex] && (
                        <div style={{ fontSize: "0.95rem" }}>
                          {/* <strong>Category :</strong>{" "} */}
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              marginTop: "4px",
                            }}
                          >
                            {row[FullCategoryListIndex].map((category, idx) => (
                              <span
                                key={idx}
                                style={{
                                  backgroundColor: "#DEF5F3",
                                  color: "black",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  // fontWeight: "600",
                                  borderColor: "#0A4D46",
                                  borderWidth: "0.5px",
                                  borderStyle: "solid",
                                  marginTop: "10px",
                                }}
                              >
                                {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Bottom Pagination & Showing */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              flexDirection: { xs: "column", sm: "row" }, // Column for small screens, row for larger
              textAlign: { xs: "center", sm: "left" }, // Center align text on small screens
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              boundaryCount={1}
              // size="small"
              shape="rounded"
              variant="outlined"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#117E76",
                },
                "& .MuiPaginationItem-outlined": {
                  backgroundColor: "#fcfcfc",
                  borderColor: "#6d6d6d",
                },
                "& .MuiPaginationItem-page.Mui-selected": {
                  backgroundColor: "#117E76",
                  color: "white",
                  borderColor: "black",
                },
                pl: { xs: 0, sm: 1 }, // Adds left padding only on tablets and up
                pb: { xs: 0, sm: 1 }, // Adds bottom padding only on tablets and up
              }}
            />
            <Typography
              variant="body2"
              sx={{
                paddingRight: { sm: "1%", xs: "0%" },
                mb: { xs: 1, sm: 0 },
              }} // Adds margin-bottom for small screens
            >
              Showing {startRow}–{endRow} of {totalRows}
            </Typography>
          </Box>
        </span>
      ) : (
        <Box sx={{ p: 1 }}>
          {/* Header: "showing 1 of 100"  */}
          <Skeleton variant="text" width={200} height={24} />
          {/* Pagination row with buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            mt={1}
          >
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width={36}
                height={36}
                sx={{ borderRadius: "50%" }}
              />
              <Skeleton
                variant="rectangular"
                width={36}
                height={36}
                sx={{ borderRadius: "50%" }}
              />
            </Box>
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width={80}
                height={36}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={36}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Stack>

          {/* Table name */}
          <Box display="flex" justifyContent="center">
            <Skeleton variant="text" width="100%" height={72} />
          </Box>

          {/* Table record (single record, 3 line data) */}
          <Box
            sx={{
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <Skeleton variant="text" height={24} width="30%" />
            <Skeleton variant="text" height={24} width="80%" />
            <Skeleton variant="text" height={24} width="40%" />
          </Box>

          {/* Bottom pagination + footer info */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width={36}
                height={36}
                sx={{ borderRadius: "50%" }}
              />
              <Skeleton
                variant="rectangular"
                width={36}
                height={36}
                sx={{ borderRadius: "50%" }}
              />
            </Box>
            <Skeleton variant="text" width={200} height={24} />
          </Stack>
        </Box>
      )}
    </TableContainer>
    // </Paper>
  );
};

export default FFTable;
